import ImageCompressor from '../libs/js/image-compressor.js';
import { occoApi } from './occoAPI-module.js';
import { alertMessagePopup, renameFileNameMutate } from './utils-module.js';
import { uploadFileToS3 } from '../../../packages/src/utils/uploadFileToS3.js';
import multiAccounts from './multiAccounts/index.ts';
import { t } from './localization.js';

export const uploadUnsupportedFormatMessageKey = 'home-upload-unsupported-format-message';
const uploadGenericErrorMessageKey = 'home-upload-generic-error-message';

export function isUnsupportedPictureFormatError(error) {
  return error?.message === uploadUnsupportedFormatMessageKey;
}

function isHeicFile(file) {
  if (/\.(heic|heif)$/i.test(file.name)) return true;
  const type = (file.type || '').toLowerCase();
  return type === 'image/heic' || type === 'image/heif';
}

async function convertHeicIfNeeded(file) {
  if (!isHeicFile(file)) return file;
  const { heicTo } = await import('heic-to');
  const jpegBlob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.9 });
  const jpegName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
  return new File([jpegBlob], jpegName, { type: 'image/jpeg', lastModified: file.lastModified });
}

function generateRandomFileName(originalFileName, { prefix = '', separator = '_', getDate = () => new Date() } = {}) {
  let fileType = '';
  if (originalFileName && originalFileName.lastIndexOf('.') !== -1) {
    fileType = originalFileName.substr(originalFileName.lastIndexOf('.') + 1);
  }
  const randomSymbols = Math.random().toString(36).substring(7);
  const prefixString = prefix ? `${prefix}${separator}` : '';

  return `${prefixString}${randomSymbols}${separator}${prepareDate()}.${fileType}`;

  function prepareDate() {
    const d = getDate();
    const yearLastDigit = d.getFullYear().toString();
    const month = (d.getMonth() + 1).toString();
    const day = d.getDate().toString();
    const hours = d.getHours().toString();
    const minutes = d.getMinutes().toString();
    const seconds = d.getSeconds().toString();
    return (
      yearLastDigit +
      separator +
      month +
      separator +
      day +
      separator +
      hours +
      separator +
      minutes +
      separator +
      seconds
    );
  }
}

export async function uploadSelfieImage(file) {
  const account = multiAccounts.getCurrentAccount();

  if (!file) {
    throw new Error('File is not defined');
  }
  if (!/\.(jpg|jpeg|heic|heif)$/i.test(file.name)) {
    throw new Error(uploadUnsupportedFormatMessageKey);
  }

  let selfieFile;
  try {
    selfieFile = await convertHeicIfNeeded(file);
  } catch {
    throw new Error(uploadUnsupportedFormatMessageKey);
  }

  return new Promise((resolve, reject) => {
    // eslint-disable-next-line no-new
    new ImageCompressor(selfieFile, {
      checkOrientation: false,
      quality: 0.9,
      maxWidth: 1500,
      convertSize: Infinity,
      mimeType: 'image/jpeg',
      success(resizedFile) {
        renameFileNameMutate(resizedFile, generateRandomFileName(selfieFile.name, { prefix: 'selfie' }));
        const now = new Date();
        const selfiePath = `${account.eventCode}/${now.getFullYear()}/${now.getMonth() + 1}/${now.getDate()}/${
          resizedFile.name
        }`;

        occoApi.profilePhotoTmpUploadAccessRetrieve(selfiePath, (err, result) => {
          if (err) {
            return reject(err);
          }
          // NOTE: Maybe we can use this callback to show the image preview
          // if (opt.callbackAdd) {
          //   opt.callbackAdd(URL.createObjectURL(resizedFile));
          // }
          uploadFileToS3(resizedFile, result.fields.key, result.url, result.fields, (imgPath) => {
            occoApi.createProfilePhoto(account.attendeeUid, imgPath, account.eventCode, (e) => {
              if (e) {
                return reject(e);
              }
              resolve();
            });
          });
        });
      },
      error(e) {
        reject(e);
      },
    });
  });
}

export async function customImageUpload(file, opt) {
  const maxWidth = 2000;
  const maxHeight = 2000;
  if (!file) return;
  if (!/\.(gif|jpg|jpeg|mp4|png|heic|heif)$/i.test(file.name)) {
    alertMessagePopup(t(uploadUnsupportedFormatMessageKey));
    return;
  }
  let convertedFile;
  try {
    convertedFile = await convertHeicIfNeeded(file);
  } catch {
    alertMessagePopup(t(uploadUnsupportedFormatMessageKey));
    return;
  }
  let fileName = convertedFile.name;
  let keepOriginName = false;
  if (opt.generateFileName) {
    fileName = generateRandomFileName(fileName, { prefix: opt.prefixFileName });
    keepOriginName = true;
  }

  // eslint-disable-next-line no-new
  new ImageCompressor(convertedFile, {
    checkOrientation: false,
    quality: 0.9,
    maxWidth,
    maxHeight,
    convertSize: Infinity,
    mimeType: 'image/jpeg',
    success(res) {
      const fileResized = res;
      occoApi.customPhotoTmpUploadAccessRetrieve(
        opt.eventName,
        fileName,
        keepOriginName,
        opt.attendeeUid,
        (err, result) => {
          if (err) {
            return alert(err.message_key);
          }
          if (opt.callbackAdd) {
            opt.callbackAdd(URL.createObjectURL(fileResized));
          }
          uploadFileToS3(fileResized, result.fields.key, result.url, result.fields, opt.callbackSuccess);
        }
      );
    },
    error() {
      alertMessagePopup(t(uploadGenericErrorMessageKey));
    },
  });
}
